import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import {
  CheckIcon,
  SelectorIcon as ChevronUpDownIcon,
  DotsVerticalIcon,
  PlusIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import { useState } from 'react';
import naturalCompare from 'string-natural-compare';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SearchInput } from '../FindAddContact/SearchInput';
import { FloatingMenu, FloatingMenuItem } from '../ui/FloatingMenu';

const byNaturalValue = (a: string, b: string) =>
  naturalCompare(a, b, { caseInsensitive: true });

type Props = {
  label?: string;
  items: string[];
  value: string | null | undefined;
  onChange: (newValue: string) => void;
  /** Called when user clicks "New Category" - opens dialog with optional pre-filled name */
  onCreateCategory?: (initialName?: string) => void;
  onRenameCategory?: (category: string) => void;
  onDeleteCategory?: (category: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  error?: string;
};

export function CategorySelect({
  label,
  items = [],
  value = '',
  onChange,
  onCreateCategory,
  onRenameCategory,
  onDeleteCategory,
  disabled = false,
  required = false,
  placeholder,
  error,
}: Props) {
  const [query, setQuery] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isMobile } = useBreakpoint();

  const handleChange = (newValue: string) => {
    // If user clicked create option, trigger the dialog
    if (newValue === '$CREATE_CATEGORY') {
      onCreateCategory?.(query.trim() || undefined);
      setQuery('');
      return;
    }
    onChange(newValue);
    setQuery(''); // Clear search after selection
  };

  const sorted = [...items].sort(byNaturalValue);

  const filtered =
    query === ''
      ? sorted
      : sorted.filter((item) =>
          item
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  // Show create option: always show "New Category" at top, or "Create [query]" when typing something new
  const showCreateOption = query.length === 0 || !items.includes(query);

  const getMenuItems = (item: string): FloatingMenuItem[] => {
    const menuItems: FloatingMenuItem[] = [];

    if (onRenameCategory) {
      menuItems.push({
        icon: <PencilIcon className='h-5 w-5' aria-hidden='true' />,
        label: 'Rename',
        onClick: () => onRenameCategory(item),
      });
    }

    if (onDeleteCategory) {
      menuItems.push({
        icon: <TrashIcon className='h-5 w-5' aria-hidden='true' />,
        label: 'Delete',
        onClick: () => onDeleteCategory(item),
      });
    }

    return menuItems;
  };

  return (
    <div
      className={classNames(
        'relative mt-0.5 w-full',
        label && !error && 'mb-5'
      )}
    >
      <Listbox value={value ?? ''} onChange={handleChange} disabled={disabled}>
        {label && (
          <Label
            className={classNames(
              disabled ? 'text-grey-30' : 'text-grey-50',
              'mb-1.5 ml-1 text-sm font-medium'
            )}
          >
            {label}
            {required && <span className='pl-0.5 text-brand'>*</span>}
          </Label>
        )}
        <ListboxButton
          className={classNames(
            'relative w-full cursor-pointer overflow-hidden rounded border border-grey-20 bg-white px-3 py-2.5 pr-10 text-left outline-none focus:border-brand focus:ring-1 focus:ring-brand',
            disabled && 'opacity-20',
            !value && 'text-gray-500',
            error && 'border-red-500'
          )}
        >
          <span className='block truncate'>
            {value || placeholder || 'Select...'}
          </span>
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
            <ChevronUpDownIcon
              className='h-5 w-5 text-gray-400'
              aria-hidden='true'
            />
          </span>
        </ListboxButton>

        <ListboxOptions
          transition
          portal={!isMobile}
          anchor={isMobile ? undefined : 'bottom start'}
          className={classNames(
            'z-[9999] my-1 mt-1 overflow-auto rounded-md bg-white pb-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
            isMobile
              ? 'absolute left-0 right-0 max-h-60'
              : 'fixed w-[var(--button-width)] [--anchor-gap:4px] [--anchor-max-height:max(20rem,var(--anchor-available-height,30rem))] [--anchor-padding:1rem]',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            'flex flex-col'
          )}
        >
          {/* Search Input */}
          <div className='sticky -top-1 z-50 -mt-1 mb-1 bg-white p-1 pb-0 pt-3'>
            <SearchInput
              className='rounded-full border-none bg-grey-3'
              inputProps={{ className: 'bg-grey-3' }}
              placeholder={label || 'Search categories...'}
              setQuery={setQuery}
              query={query}
            />
          </div>

          <div className='overflow-y-auto py-1'>
            {/* Create Option - Always visible at top when onCreateCategory is provided */}
            {onCreateCategory && showCreateOption && (
              <ListboxOption
                value='$CREATE_CATEGORY'
                className='relative cursor-default select-none py-2 pl-10 pr-4 text-primary active:bg-shade-12 data-[focus]:bg-shade-10 data-[selected]:bg-shade-15'
              >
                {({ focus }) => (
                  <>
                    <span className='block truncate text-base'>
                      {query ? `Create "${query}"` : 'New Tag Category'}
                    </span>
                    <span
                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                        focus && 'text-brand'
                      }`}
                    >
                      <PlusIcon className='h-5 w-5' aria-hidden='true' />
                    </span>
                  </>
                )}
              </ListboxOption>
            )}

            {/* Category Options */}
            {filtered.map((item) => (
              <ListboxOption
                key={item}
                value={item}
                className='group relative cursor-default select-none py-1 pl-10 pr-4 text-primary active:bg-shade-12 data-[focus]:bg-shade-10 data-[selected]:bg-shade-15'
                onMouseEnter={() => setHoveredItem(item)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                {({ selected }) => (
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center'>
                      {selected && (
                        <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-brand'>
                          <CheckIcon className='h-5 w-5' aria-hidden='true' />
                        </span>
                      )}
                      <span
                        className={classNames(
                          'block truncate text-base',
                          selected ? 'font-medium' : 'font-normal'
                        )}
                      >
                        {item}
                      </span>
                    </div>

                    {/* Ellipsis Menu for Rename/Delete - fixed size wrapper to prevent height shifts */}
                    {(onRenameCategory || onDeleteCategory) && (
                      <div
                        className='h-8 w-8 flex-shrink-0'
                        // Stop all pointer events from bubbling to ListboxOption
                        // This prevents the Listbox from selecting the item when interacting with the menu
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                      >
                        {/* Only render FloatingMenu when hovered to reset menu state on hover change */}
                        {hoveredItem === item && (
                          <FloatingMenu
                            items={getMenuItems(item)}
                            buttonText={
                              <DotsVerticalIcon className='h-4 w-4' />
                            }
                            buttonClassName={(isOpen) =>
                              classNames(
                                'flex h-8 w-8 items-center justify-center rounded-full',
                                isOpen
                                  ? 'bg-shade-15 text-primary'
                                  : 'hover:bg-white/70 hover:text-primary text-secondary'
                              )
                            }
                            usePortal={false}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </ListboxOption>
            ))}
          </div>
        </ListboxOptions>
      </Listbox>
      {error && <p className='ErrorMessage mb-5 mt-1'>{error}</p>}
    </div>
  );
}
